import React, { useState, useEffect } from 'react';
// import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ArrowLeft, Loader2, ShieldAlert, Users, Eye, CheckCircle, XCircle, Folder } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
// import { createPageUrl } from '@/utils';
import { Toaster } from "sonner";
import { format } from "date-fns";
import { useAuth } from '../context/AuthContext';
// import TermsModal from '@/components/dashboard/TermsModal';
// import * as userApi from '@/components/api/userApi';
// import * as adminApi from '@/components/api/adminApi';

// -- Mock API placeholders until actual endpoints are available --
const adminApi = {
    getAllUsers: async () => [
        {
            user_id: 'u1',
            full_name: 'John Doe',
            email: 'john@example.com',
            role: 'student',
            accepted_terms_version: '2.0',
            created_date: new Date(Date.now() - 86400000 * 10).toISOString()
        },
        {
            user_id: 'u2',
            full_name: 'Admin User',
            email: 'admin@example.com',
            role: 'admin',
            accepted_terms_version: '2.0',
            created_date: new Date(Date.now() - 86400000 * 365).toISOString()
        },
        {
            user_id: 'u3',
            full_name: 'Jane Smith',
            email: 'jane@example.com',
            role: 'student',
            accepted_terms_version: null,
            created_date: new Date(Date.now() - 86400000 * 2).toISOString()
        }
    ],
    getUserFolders: async (userId) => {
        // Mock returning folders for a specific user
        return {
            full_name: userId === 'u1' ? 'John Doe' : 'Jane Smith',
            email: userId === 'u1' ? 'john@example.com' : 'jane@example.com',
            folders: [
                { folder_id: '1', name: 'Gold Scalp', code: 'GOLD_SC', is_active: true, user_is_following: true },
                { folder_id: '2', name: 'Major Pairs', code: 'MAJORS', is_active: true, user_is_following: false },
                { folder_id: '3', name: 'Indices', code: 'INDICES', is_active: false, user_is_following: false }
            ]
        };
    }
};

const userApi = {
    getTermsStatus: async (id) => ({ accepted: true, terms_version_id: 'v2', terms_content: "Terms here..." }),
    acceptTerms: async (id, version) => true
};
// ------------------------------------------------

export default function AdminUsers() {
    const { user, loading: authLoading } = useAuth();
    const [userLoading, setUserLoading] = useState(true);
    const [termsAccepting, setTermsAccepting] = useState(false);
    const [termsStatus, setTermsStatus] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [folderDialogOpen, setFolderDialogOpen] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        // Simulate user loading
        if (!authLoading && user) {
            setUserLoading(false);
            setTermsStatus({ accepted: true });
        } else if (!authLoading && !user) {
            setUserLoading(false);
        }
    }, [user, authLoading]);

    const hasAcceptedTerms = termsStatus?.accepted === true;
    const isAdmin = user?.role === 'admin';

    const { data: users = [], isLoading } = useQuery({
        queryKey: ['adminUsers'],
        queryFn: () => adminApi.getAllUsers(),
        enabled: !!(hasAcceptedTerms && isAdmin),
    });

    const { data: userFolders, isLoading: foldersLoading } = useQuery({
        queryKey: ['adminUserFolders', selectedUserId],
        queryFn: () => adminApi.getUserFolders(selectedUserId),
        enabled: !!selectedUserId && hasAcceptedTerms && isAdmin,
    });

    const acceptTerms = async () => {
        setTermsAccepting(true);
        // await userApi.acceptTerms(user.id, termsStatus.terms_version_id);
        setTermsStatus({ ...termsStatus, accepted: true });
        setTermsAccepting(false);
    };

    const openFolderDialog = (userId) => {
        setSelectedUserId(userId);
        setFolderDialogOpen(true);
    };

    if (authLoading || userLoading) {
        return (
            <div className="h-screen bg-slate-900 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            </div>
        );
    }

    if (!hasAcceptedTerms) {
        return (
            <div className="h-screen bg-slate-900 flex items-center justify-center text-white">
                <div className="text-center">
                    <h2 className="text-xl mb-4">Terms Acceptance Required</h2>
                    <Button onClick={acceptTerms} disabled={termsAccepting}>
                        {termsAccepting ? "Accepting..." : "Accept Mock Terms"}
                    </Button>
                </div>
            </div>
        )
    }

    if (!isAdmin) {
        return (
            <div className="h-screen bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h1 className="text-xl font-bold text-white mb-2">Access Denied</h1>
                    <p className="text-slate-400 mb-4">Only administrators can access this page.</p>
                    <Link to="/dashboard">
                        <Button className="bg-blue-600 hover:bg-blue-700">Go to Dashboard</Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-900 p-6">
            <Toaster richColors position="top-right" />

            <div className="max-w-6xl mx-auto">
                <div className="flex items-center gap-4 mb-8">

                    <div>
                        <h1 className="text-2xl font-bold text-white">User Management</h1>
                        <p className="text-slate-400 text-sm mt-1">View all users and their folder subscriptions (read-only)</p>
                    </div>
                </div>

                <Card className="bg-slate-950 border-slate-800">
                    <CardHeader className="border-b border-slate-800">
                        <div className="flex items-center gap-3">
                            <Users className="h-5 w-5 text-blue-400" />
                            <CardTitle className="text-white">All Users ({users.length})</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-slate-800 hover:bg-transparent">
                                    <TableHead className="text-slate-400">User</TableHead>
                                    <TableHead className="text-slate-400">Role</TableHead>
                                    <TableHead className="text-slate-400">Terms Accepted</TableHead>
                                    <TableHead className="text-slate-400">Joined</TableHead>
                                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                                            Loading users...
                                        </TableCell>
                                    </TableRow>
                                ) : users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                                            <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                            No users found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map(u => (
                                        <TableRow key={u.user_id} className="border-slate-800">
                                            <TableCell>
                                                <div>
                                                    <span className="font-medium text-white">{u.full_name || 'No name'}</span>
                                                    <p className="text-sm text-slate-500">{u.email}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={u.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-slate-700 text-slate-300'}>
                                                    {u.role}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {u.accepted_terms_version ? (
                                                    <div className="flex items-center gap-2">
                                                        <CheckCircle className="h-4 w-4 text-emerald-400" />
                                                        <span className="text-emerald-400 text-sm">{u.accepted_terms_version}</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <XCircle className="h-4 w-4 text-red-400" />
                                                        <span className="text-red-400 text-sm">Not accepted</span>
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-slate-400 text-sm">
                                                    {format(new Date(u.created_date), 'MMM d, yyyy')}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openFolderDialog(u.user_id)}
                                                    className="text-slate-400 hover:text-white hover:bg-slate-800"
                                                >
                                                    <Eye className="h-4 w-4 mr-1" />
                                                    View Folders
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>

            {/* User Folders Dialog */}
            <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-white">User Folder Subscriptions</DialogTitle>
                    </DialogHeader>
                    {foldersLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
                        </div>
                    ) : userFolders ? (
                        <div className="space-y-4">
                            <div className="text-sm text-slate-400">
                                <strong>{userFolders.full_name || userFolders.email}</strong>
                            </div>
                            <div className="space-y-2">
                                {userFolders.folders.map(folder => (
                                    <div
                                        key={folder.folder_id}
                                        className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Folder className="h-4 w-4 text-blue-400" />
                                            <div>
                                                <span className="text-white text-sm">{folder.name}</span>
                                                <span className="text-slate-500 text-xs ml-2">({folder.code})</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {!folder.is_active && (
                                                <Badge className="bg-red-500/20 text-red-400 text-xs">Disabled</Badge>
                                            )}
                                            <Badge className={folder.user_is_following ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'}>
                                                {folder.user_is_following ? 'Following' : 'Not Following'}
                                            </Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-slate-500 mt-4">
                                Note: This is read-only. Admin cannot modify user follow settings.
                            </p>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}
